import React, { createContext, useContext, useState, useEffect } from 'react';

export type PortalLanguage = 'fr' | 'en' | 'ar';

interface TeacherPortalLanguageContextType {
  language: PortalLanguage;
  setLanguage: (lang: PortalLanguage) => void;
  t: (key: string, ...args: any[]) => string;
  dir: 'ltr' | 'rtl';
}

const translations: Record<PortalLanguage, Record<string, string>> = {
  fr: {
    'portal.teacher.brand': 'Blobtic Academy',
    'portal.teacher.title': 'Portail Enseignant',
    'portal.teacher.subtitle': 'Gestion des présences, scan des badges, séances et suivi financier',
    'portal.teacher.login_label': 'Identifiant (Email ou Téléphone)',
    'portal.teacher.login_placeholder': 'Ex: prof@academie.com ou 0550123456',
    'portal.teacher.password_label': 'Mot de passe',
    'portal.teacher.password_placeholder': 'Votre mot de passe',
    'portal.teacher.btn_login': 'Se connecter au portail',
    'portal.teacher.or_sso': 'Ou connectez-vous avec',
    'portal.teacher.sso_google': 'Continuer avec Google',
    'portal.teacher.sso_microsoft': 'Continuer avec Microsoft',
    'portal.teacher.select_academy_title': 'Choisissez votre Académie',
    'portal.teacher.select_academy_desc': 'Vous enseignez dans plusieurs établissements. Sélectionnez l\'académie à gérer :',
    'portal.teacher.btn_access_academy': 'Accéder à cette académie',
    'portal.teacher.logout': 'Déconnexion',
    'portal.teacher.welcome': 'Bonjour, Professeur {0}',
    'portal.teacher.tab_overview': 'Vue d\'ensemble',
    'portal.teacher.tab_attendance': 'Feuille de Présence',
    'portal.teacher.tab_sessions': 'Mes Séances',
    'portal.teacher.tab_groups': 'Mes Groupes',
    'portal.teacher.tab_finances': 'Mes Rémunérations',
    'portal.teacher.tab_absence_requests': 'Demandes d\'absence',
    'portal.teacher.attendance_permission_denied': 'La saisie des présences n\'est pas activée sur votre compte enseignant. Veuillez contacter l\'administration pour l\'activer.',
    'portal.teacher.active_session_label': 'Séance en cours / sélectionnée',
    'portal.teacher.select_session_prompt': 'Veuillez sélectionner une séance pour démarrer l\'appel ou le scan des badges.',
    'portal.teacher.scan_badge_btn': 'Scanner un Badge Étudiant',
    'portal.teacher.stop_scanner_btn': 'Arrêter la caméra',
    'portal.teacher.manual_attendance_btn': 'Saisie manuelle',
    'portal.teacher.scanner_instruction': 'Dirigez la caméra vers le code-barres ou le QR code du badge étudiant (Ex: BLO-STD-...)',
    'portal.teacher.scan_success_present': 'Badge scanné avec succès ! {0} marqué Présent.',
    'portal.teacher.scan_error': 'Code badge non reconnu ou étudiant non inscrit à cette séance.',
    'portal.teacher.student_col': 'Élève',
    'portal.teacher.status_col': 'Présence',
    'portal.teacher.arrival_col': 'Heure d\'arrivée',
    'portal.teacher.action_col': 'Actions',
    'portal.teacher.mark_present': 'Présent',
    'portal.teacher.mark_absent': 'Absent',
    'portal.teacher.mark_late': 'En retard',
    'portal.teacher.mark_excused': 'Excusé',
    'portal.teacher.justify_absence': 'Justifier l\'absence',
    'portal.teacher.justification_dialog_title': 'Justification d\'absence pour {0}',
    'portal.teacher.justification_reason_label': 'Motif / Raison de l\'absence',
    'portal.teacher.justification_file_label': 'Certificat / Document justificatif',
    'portal.teacher.save_justification': 'Enregistrer le justificatif',
    'portal.teacher.btn_request_absence': 'Déposer une demande d\'absence',
    'portal.teacher.absence_dialog_title': 'Demande d\'absence pour une séance',
    'portal.teacher.absence_session_label': 'Séance concernée',
    'portal.teacher.absence_start_date': 'Date de début',
    'portal.teacher.absence_end_date': 'Date de fin (optionnel)',
    'portal.teacher.absence_reason': 'Motif détaillé de l\'absence',
    'portal.teacher.absence_submit_btn': 'Transmettre la demande',
    'portal.teacher.absence_success': 'Votre demande d\'absence a été soumise avec succès. L\'administration a été notifiée.',
    'portal.teacher.finances_title': 'Suivi des honoraires & rémunérations',
    'portal.teacher.rate_type': 'Mode de calcul',
    'portal.teacher.hourly_rate': 'Taux / Montant unitaire',
    'portal.teacher.hours_month': 'Heures effectuées ce mois',
    'portal.teacher.estimated_amount': 'Montant prévisionnel ce mois',
    'portal.teacher.absence_requests_list': 'Historique de mes demandes d\'absence',
    'portal.teacher.status_pending': 'En attente',
    'portal.teacher.status_approved': 'Validée',
    'portal.teacher.status_rejected': 'Refusée',
    'portal.teacher.group_students_count': '{0} élèves inscrits',
    'portal.teacher.change_academy': 'Changer d\'académie',
    'portal.teacher.cancel': 'Annuler',
    'portal.teacher.close': 'Fermer',
    'portal.teacher.all_present': 'Marquer tous présents',
    'portal.teacher.absence_error_title': 'Erreur de demande d\'absence',
    'portal.teacher.absence_error': 'Impossible de transmettre votre demande d\'absence. Veuillez vérifier les informations ou contacter l\'administration.',
    'portal.teacher.no_students_enrolled': 'Aucun élève inscrit dans ce groupe.',
    'portal.teacher.status_not_marked': 'Non émargé',
  },
  en: {
    'portal.teacher.brand': 'Blobtic Academy',
    'portal.teacher.title': 'Teacher Portal',
    'portal.teacher.subtitle': 'Attendance tracking, badge scanning, classes and payroll overview',
    'portal.teacher.login_label': 'Login ID (Email or Phone)',
    'portal.teacher.login_placeholder': 'e.g. teacher@academy.com or 0550123456',
    'portal.teacher.password_label': 'Password',
    'portal.teacher.password_placeholder': 'Your password',
    'portal.teacher.btn_login': 'Sign in to portal',
    'portal.teacher.or_sso': 'Or sign in with',
    'portal.teacher.sso_google': 'Continue with Google',
    'portal.teacher.sso_microsoft': 'Continue with Microsoft',
    'portal.teacher.select_academy_title': 'Select your Academy',
    'portal.teacher.select_academy_desc': 'You teach across multiple institutions. Please choose the academy to manage:',
    'portal.teacher.btn_access_academy': 'Access this academy',
    'portal.teacher.logout': 'Sign out',
    'portal.teacher.welcome': 'Welcome, Teacher {0}',
    'portal.teacher.tab_overview': 'Overview',
    'portal.teacher.tab_attendance': 'Attendance Sheet',
    'portal.teacher.tab_sessions': 'My Sessions',
    'portal.teacher.tab_groups': 'My Groups',
    'portal.teacher.tab_finances': 'My Compensation',
    'portal.teacher.tab_absence_requests': 'Absence Requests',
    'portal.teacher.attendance_permission_denied': 'Attendance taking is not enabled for your teacher profile. Please contact the administration.',
    'portal.teacher.active_session_label': 'Current / Selected Session',
    'portal.teacher.select_session_prompt': 'Please select a session to take attendance or scan student badges.',
    'portal.teacher.scan_badge_btn': 'Scan Student Badge',
    'portal.teacher.stop_scanner_btn': 'Stop Camera',
    'portal.teacher.manual_attendance_btn': 'Manual Check-in',
    'portal.teacher.scanner_instruction': 'Point camera at the barcode or QR code on the student badge (e.g. BLO-STD-...)',
    'portal.teacher.scan_success_present': 'Badge scanned successfully! {0} marked Present.',
    'portal.teacher.scan_error': 'Unrecognized badge code or student not enrolled in this session.',
    'portal.teacher.student_col': 'Student',
    'portal.teacher.status_col': 'Attendance',
    'portal.teacher.arrival_col': 'Arrival Time',
    'portal.teacher.action_col': 'Actions',
    'portal.teacher.mark_present': 'Present',
    'portal.teacher.mark_absent': 'Absent',
    'portal.teacher.mark_late': 'Late',
    'portal.teacher.mark_excused': 'Excused',
    'portal.teacher.justify_absence': 'Justify Absence',
    'portal.teacher.justification_dialog_title': 'Absence Justification for {0}',
    'portal.teacher.justification_reason_label': 'Reason for absence',
    'portal.teacher.justification_file_label': 'Medical Note / Document',
    'portal.teacher.save_justification': 'Save Justification',
    'portal.teacher.btn_request_absence': 'Submit Absence Request',
    'portal.teacher.absence_dialog_title': 'Session Absence Request',
    'portal.teacher.absence_session_label': 'Target Session',
    'portal.teacher.absence_start_date': 'Start Date',
    'portal.teacher.absence_end_date': 'End Date (optional)',
    'portal.teacher.absence_reason': 'Detailed reason for absence',
    'portal.teacher.absence_submit_btn': 'Submit Request',
    'portal.teacher.absence_success': 'Your absence request has been submitted. The administration has been notified.',
    'portal.teacher.finances_title': 'Remuneration & Compensation Tracker',
    'portal.teacher.rate_type': 'Rate Type',
    'portal.teacher.hourly_rate': 'Unit Rate',
    'portal.teacher.hours_month': 'Hours Taught This Month',
    'portal.teacher.estimated_amount': 'Estimated Pay This Month',
    'portal.teacher.absence_requests_list': 'My Absence Requests History',
    'portal.teacher.status_pending': 'Pending',
    'portal.teacher.status_approved': 'Approved',
    'portal.teacher.status_rejected': 'Rejected',
    'portal.teacher.group_students_count': '{0} enrolled students',
    'portal.teacher.change_academy': 'Switch Academy',
    'portal.teacher.cancel': 'Cancel',
    'portal.teacher.close': 'Close',
    'portal.teacher.all_present': 'Mark All Present',
    'portal.teacher.absence_error_title': 'Absence Request Error',
    'portal.teacher.absence_error': 'Unable to submit your absence request. Please verify the details or contact the administration.',
    'portal.teacher.no_students_enrolled': 'No students enrolled in this group.',
    'portal.teacher.status_not_marked': 'Not marked',
  },
  ar: {
    'portal.teacher.brand': 'أكاديمية بلوبتيك',
    'portal.teacher.title': 'بوابة الأستاذ',
    'portal.teacher.subtitle': 'تسجيل الحضور والغياب، مسح الشارات، متابعة الحصص والمستحقات المالية',
    'portal.teacher.login_label': 'المعرّف (البريد الإلكتروني أو الهاتف)',
    'portal.teacher.login_placeholder': 'مثال: prof@academie.com أو 0550123456',
    'portal.teacher.password_label': 'كلمة المرور',
    'portal.teacher.password_placeholder': 'كلمة المرور الخاصة بك',
    'portal.teacher.btn_login': 'تسجيل الدخول إلى البوابة',
    'portal.teacher.or_sso': 'أو تسجيل الدخول عبر',
    'portal.teacher.sso_google': 'المتابعة بواسطة Google',
    'portal.teacher.sso_microsoft': 'المتابعة بواسطة Microsoft',
    'portal.teacher.select_academy_title': 'اختر المؤسسة أو الأكاديمية',
    'portal.teacher.select_academy_desc': 'تقوم بالتدريس في عدة مؤسسات. يرجى اختيار المؤسسة لإدارتها:',
    'portal.teacher.btn_access_academy': 'الدخول إلى هذه المؤسسة',
    'portal.teacher.logout': 'تسجيل الخروج',
    'portal.teacher.welcome': 'مرحباً، الأستاذ {0}',
    'portal.teacher.tab_overview': 'نظرة عامة',
    'portal.teacher.tab_attendance': 'ورقة الحضور والغياب',
    'portal.teacher.tab_sessions': 'حصصي التعليمية',
    'portal.teacher.tab_groups': 'أفواجي',
    'portal.teacher.tab_finances': 'مستحقاتي المالية',
    'portal.teacher.tab_absence_requests': 'طلبات الغياب',
    'portal.teacher.attendance_permission_denied': 'خاصية تسجيل الحضور غير مفعلة لحسابك. يرجى التواصل مع إدارة المؤسسة لتفعيلها.',
    'portal.teacher.active_session_label': 'الحصة الجارية / المختارة',
    'portal.teacher.select_session_prompt': 'يرجى اختيار حصة لبدء تسجيل الحضور أو مسح شارات التلاميذ.',
    'portal.teacher.scan_badge_btn': 'مسح شارة الطالب',
    'portal.teacher.stop_scanner_btn': 'إيقاف الكاميرا',
    'portal.teacher.manual_attendance_btn': 'تسجيل يدوي',
    'portal.teacher.scanner_instruction': 'وجّه الكاميرا نحو الباركود أو رمز الاستجابة السريعة على شارة الطالب (مثال: BLO-STD-...)',
    'portal.teacher.scan_success_present': 'تم مسح الشارة بنجاح! تم تسجيل {0} حاضر.',
    'portal.teacher.scan_error': 'رمز الشارة غير معترف به أو الطالب غير مسجل في هذه الحصة.',
    'portal.teacher.student_col': 'الطالب',
    'portal.teacher.status_col': 'الحضور',
    'portal.teacher.arrival_col': 'وقت الوصول',
    'portal.teacher.action_col': 'إجراءات',
    'portal.teacher.mark_present': 'حاضر',
    'portal.teacher.mark_absent': 'غائب',
    'portal.teacher.mark_late': 'متأخر',
    'portal.teacher.mark_excused': 'معذور',
    'portal.teacher.justify_absence': 'تبرير الغياب',
    'portal.teacher.justification_dialog_title': 'تبرير غياب التلميذ {0}',
    'portal.teacher.justification_reason_label': 'سبب الغياب',
    'portal.teacher.justification_file_label': 'الشهادة الطبية أو الوثيقة التبريرية',
    'portal.teacher.save_justification': 'حفظ التبرير',
    'portal.teacher.btn_request_absence': 'تقديم طلب غياب',
    'portal.teacher.absence_dialog_title': 'طلب غياب عن حصة تعليمية',
    'portal.teacher.absence_session_label': 'الحصة المعنية',
    'portal.teacher.absence_start_date': 'تاريخ البداية',
    'portal.teacher.absence_end_date': 'تاريخ النهاية (اختياري)',
    'portal.teacher.absence_reason': 'السبب المفصل للغياب',
    'portal.teacher.absence_submit_btn': 'إرسال الطلب',
    'portal.teacher.absence_success': 'تم إرسال طلب الغياب بنجاح وتم إشعار الإدارة.',
    'portal.teacher.finances_title': 'متابعة المستحقات والأتعاب',
    'portal.teacher.rate_type': 'طريقة الحساب',
    'portal.teacher.hourly_rate': 'المعدل / الأجر المرجعي',
    'portal.teacher.hours_month': 'الساعات المنجزة هذا الشهر',
    'portal.teacher.estimated_amount': 'المبلغ التقديري لهذا الشهر',
    'portal.teacher.absence_requests_list': 'سجل طلبات الغياب السابقة',
    'portal.teacher.status_pending': 'قيد المعالجة',
    'portal.teacher.status_approved': 'مقبول',
    'portal.teacher.status_rejected': 'مرفوض',
    'portal.teacher.group_students_count': '{0} تلميذ مسجل',
    'portal.teacher.change_academy': 'تغيير الأكاديمية',
    'portal.teacher.cancel': 'إلغاء',
    'portal.teacher.close': 'إغلاق',
    'portal.teacher.all_present': 'تحديد الكل حاضر',
    'portal.teacher.absence_error_title': 'خطأ في طلب الغياب',
    'portal.teacher.absence_error': 'تعذر إرسال طلب الغياب. يرجى التحقق من البيانات أو التواصل مع الإدارة.',
    'portal.teacher.no_students_enrolled': 'لا يوجد تلاميذ مسجلون في هذا الفوج.',
    'portal.teacher.status_not_marked': 'غير مسجل',
  }
};

const TeacherPortalLanguageContext = createContext<TeacherPortalLanguageContextType | undefined>(undefined);

export const TeacherPortalLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<PortalLanguage>(() => {
    return (localStorage.getItem('blobtic_portal_teacher_lang') as PortalLanguage) || 'fr';
  });

  const setLanguage = (lang: PortalLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('blobtic_portal_teacher_lang', lang);
  };

  const dir: 'ltr' | 'rtl' = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [dir, language]);

  const t = (key: string, ...args: any[]): string => {
    const text = translations[language]?.[key] || translations['fr']?.[key] || key;
    if (!args.length) return text;
    return text.replace(/\{(\d+)\}/g, (_match, index) => {
      const idx = parseInt(index, 10);
      return args[idx] !== undefined ? String(args[idx]) : '';
    });
  };

  return (
    <TeacherPortalLanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      <div dir={dir} style={{ minHeight: '100vh', width: '100%' }}>
        {children}
      </div>
    </TeacherPortalLanguageContext.Provider>
  );
};

export const useTeacherPortalLanguage = (): TeacherPortalLanguageContextType => {
  const context = useContext(TeacherPortalLanguageContext);
  if (!context) {
    throw new Error('useTeacherPortalLanguage must be used within a TeacherPortalLanguageProvider');
  }
  return context;
};
